import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Button, Chip, CircularProgress, Divider, Stack, Typography, screenSizeBigger, StickyTooltip} from 'views/components';
import {Check} from 'views/icons';
import {CommandResponseView, ConnectionBadge, HoverToolBox, Translate, translate, useCommand} from 'views/module';
import CommandTextViewer from '../../../../module/task/components/CommandTextViewer';

import {TASK_TYPE_CONFIG} from '../../utils/taskTypeConfig';

import moment from 'moment';

const TaskItem = (props) => {
  const {
    task,
    handleSetStatusToComplete,
    onClickEditTask,
    removeTask,
    playbooks,
    requester,
    skills,
    statusProgressWrap,
  } = props;
  const isStatusComplete = task.status === 'COMPLETE';
  const largerScreen = screenSizeBigger('sm');

  const isDueDateADate = moment(task.dueDate, moment.ISO_8601, true).isValid();
  const dueDateMoment = isDueDateADate ? moment(task.dueDate) : null;
  const isOverdue = isDueDateADate ?
    dueDateMoment.isBefore(moment()) :
    false;
  const overdueDayCount = isDueDateADate ?
    moment().startOf('day').diff(dueDateMoment.clone().startOf('day'), 'days') :
    0;
  const maxDisplayedAvatarIcons = 5;
  const connections = task.connectionInfo || [];
  const ownerRns = Array.isArray(task.ownerRns) ? task.ownerRns :
    Array.isArray(task.ownerRn) ? task.ownerRn :
      task.ownerRn ? [task.ownerRn] : [];
  const owners = (task.ownerInfo || []).length > 0 ?
    task.ownerInfo :
    ownerRns
        .filter(Boolean)
        .map((ownerRn) => ({userRn: ownerRn, name: ownerRn}));
  const visibleConnections = connections.slice(0, maxDisplayedAvatarIcons);
  const hiddenConnections = connections.slice(maxDisplayedAvatarIcons);
  const hasDueDateDisplay = isDueDateADate || Boolean(task.dueDate && !isDueDateADate);
  const descriptionText = typeof task.description === 'string' ?
    task.description :
    task.description?.blob || translate('tasks.MyDay.TaskItem.Description.Empty');
  const playbook = playbooks.find((entry) => entry.playbookRn === task.playbookRn) || null;

  const [toolbarVisible, setToolbarVisible] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const rightEdgeAnchorRef = React.useRef(null);

  const command = useCommand({
    requester,
    skills,
    statusProgressWrap,
    task,
  });

  const handleMouseEnter = () => {
    if (largerScreen) {
      setToolbarVisible(true);
      setAnchorEl(rightEdgeAnchorRef.current);
    }
  };

  const handleMouseLeave = () => {
    if (largerScreen) {
      setToolbarVisible(false);
      setAnchorEl(null);
    }
  };

  React.useEffect(() => {
    setToolbarVisible(false);
    setAnchorEl(null);
  }, [largerScreen]);

  const getConnectionDisplayName = (connection) => [connection.firstName, connection.lastName].filter(Boolean).join(' ');
  const handleApproveAndSendSuccess = () => {
    handleSetStatusToComplete(task.taskRn, isStatusComplete);
  };

  return (
    <Stack
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        position: 'relative',
        opacity: isStatusComplete ? 0.45 : 1,
      }}
    >
      <Stack
        spacing={1.25}
        sx={{
          py: 2,
          pr: 3,
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
        >
          <Box
            onClick={() => handleSetStatusToComplete(task.taskRn, isStatusComplete)}
            sx={{
              'width': 24,
              'height': 24,
              'borderRadius': '50%',
              'border': '1.5px solid',
              'borderColor': isStatusComplete ? 'success.main' : 'divider',
              'backgroundColor': isStatusComplete ? 'success.main' : 'transparent',
              'display': 'flex',
              'alignItems': 'center',
              'justifyContent': 'center',
              'cursor': 'pointer',
              '& .todo-check': {
                transition: 'color 120ms ease',
                color: isStatusComplete ? 'common.white' : 'transparent',
                fontSize: 10,
              },
              '&:hover': {
                borderColor: isStatusComplete ? 'success.main' : 'success.main',
              },
              '&:hover .todo-check': {
                color: isStatusComplete ? 'common.white' : 'success.main',
              },
            }}
          >
            <Check className="todo-check" />
          </Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              flex: 1,
              minWidth: 0,
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                flexWrap: 'wrap',
                flex: 1,
                minWidth: 0,
              }}
            >
              {
                task?.type && (() => {
                  const type = task.type;
                  const typeConfig = TASK_TYPE_CONFIG[type] ?? TASK_TYPE_CONFIG.UNKNOWN;

                  return (
                    <Chip
                      key={type}
                      variant="outlined"
                      label={typeConfig.labelKey ? translate(typeConfig.labelKey) : type}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        ...typeConfig.chipSx,
                        borderWidth: 1,
                      }}
                    />
                  );
                })()
              }
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  textDecoration: isStatusComplete ? 'line-through' : 'none',
                  minWidth: 0,
                }}
              >
                <CommandTextViewer
                  value={task.title}
                  owners={owners}
                  connections={connections}
                  type={task.type}
                />
                {
                  command.canRunCommand &&
                    <Box
                      component='span'
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        ml: 1,
                      }}
                    >
                      <Box
                        component='span'
                        sx={{
                          color: 'text.secondary',
                          fontWeight: 400,
                        }}
                      >
                        -
                      </Box>
                      {
                        command.isExecutingCommand &&
                          <CircularProgress
                            size={20}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#4B4843',
                              minWidth: '20px',
                              marginLeft: '12px',
                            }}
                          />
                      }
                      {
                        !command.isExecutingCommand &&
                          <Button
                            variant='outlined'
                            size='small'
                            onClick={command.runCommand}
                            sx={{
                              minWidth: '30px',
                              px: '12px',
                              py: '8px',
                              borderRadius: '7px',
                              borderColor: '#D8D4CE',
                              background: '#FCFCFC',
                              color: '#4B4843',
                              fontSize: '11px',
                              fontWeight: 500,
                              lineHeight: '100%',
                              textTransform: 'none',
                            }}
                          >
                            <Translate expression='tasks.Common.Run' />
                          </Button>
                      }
                    </Box>
                }
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        <Stack
          spacing={1}
          sx={{
            pl: '40px',
          }}
        >
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            <CommandTextViewer
              value={descriptionText}
              owners={owners}
              connections={connections}
              type={task.type}
            />
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              flexWrap: 'wrap',
            }}
          >
            {
              isDueDateADate &&
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: isOverdue && !isStatusComplete ? 'error.main' : 'text.secondary',
                }}
              >
                {
                  overdueDayCount === 1 && (
                    <Translate
                      expression="tasks.MyDay.TaskItem.Due.YesterdayLabel"
                      substitute={{time: dueDateMoment.format('h:mmA')}}
                    />
                  )
                }

                {
                  overdueDayCount > 1 &&
                    <Translate
                      expression="tasks.MyDay.TaskItem.Due.DaysAgoLabel"
                      substitute={{
                        days: overdueDayCount,
                        time: dueDateMoment.format('h:mmA'),
                      }}
                    />
                }
                {
                  overdueDayCount < 1 &&
                    <Translate
                      expression="tasks.MyDay.TaskItem.Due.Label"
                      substitute={{time: dueDateMoment.format('h:mmA')}}
                    />
                }
              </Typography>
            }
            {
              (task.dueDate && !isDueDateADate) &&
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: 'text.secondary',
                }}
              >
                {task.dueDate}
              </Typography>
            }
            <Box
              sx={{
                display: hasDueDateDisplay && connections.length > 0 ? 'block' : 'none',
                height: 4,
                width: 4,
                borderRadius: '50%',
                backgroundColor: 'text.secondary',
              }}
            />
            {
              connections.length > 0 &&
                <Stack
                  direction="row"
                  spacing={-.5}
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  {
                    visibleConnections.map((connection, index) => (
                      <ConnectionBadge
                        key={connection.connectionRn}
                        connection={connection}
                        sx={{
                          width: '28px',
                          height: '28px',
                          position: 'relative',
                          zIndex: index + 1,
                          fontSize: '13px',
                          outline: '2px solid',
                          outlineColor: 'background.paper',
                        }}
                      />
                    ))
                  }
                  {
                    hiddenConnections.length > 0 &&
                      <StickyTooltip
                        title={hiddenConnections.map((connection) => getConnectionDisplayName(connection)).join(', ')}
                      >
                        <Box
                          sx={{
                            width: '28px',
                            height: '28px',
                            position: 'relative',
                            zIndex: visibleConnections.length + 1,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#E2DEDA',
                            color: 'text.secondary',
                            fontSize: '11px',
                            fontWeight: 600,
                            outline: '2px solid',
                            outlineColor: 'background.paper',
                          }}
                        >
                          +{hiddenConnections.length}
                        </Box>
                      </StickyTooltip>
                  }
                </Stack>
            }
            {
              Boolean(playbook) &&
                <>
                  <Box
                    sx={{
                      height: 4,
                      width: 4,
                      borderRadius: '50%',
                      backgroundColor: 'text.secondary',
                    }}
                  />
                  <Typography
                    variant='caption'
                    component='button'
                    onClick={() => window.open(`/playbooks?selectedPlaybookRn=${encodeURIComponent(playbook.playbookRn)}`)}
                    sx={{
                      'background': 'none',
                      'border': 'none',
                      'color': '#DA7955',
                      'cursor': 'pointer',
                      'fontSize': '12px',
                      'p': 0,
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    /{playbook.title}
                  </Typography>
                </>
            }
          </Stack>
          {
            command.response &&
              <CommandResponseView
                communicationTarget={command.communicationTarget}
                requester={requester}
                response={command.response}
                onApproveAndSendSuccess={handleApproveAndSendSuccess}
                onDiscard={command.discard}
                isRegenerating={command.isExecutingCommand}
                onRegenerate={command.regenerate}
              />
          }
        </Stack>
      </Stack>
      <Box
        ref={rightEdgeAnchorRef}
        sx={{
          position: 'absolute',
          right: -100,
          top: 20,
          width: 0,
          height: 0,
        }}
      />
      <HoverToolBox
        toolBar={toolbarVisible}
        anchorEl={anchorEl}
        onClickEdit={() => onClickEditTask(task)}
        onClickDelete={() => removeTask(task)}
        hideTool={{
          emoji: true,
          copy: true,
          edit: false,
          delete: false,
          reply: true,
        }}
      />
      <Divider />
    </Stack>
  );
};

TaskItem.propTypes = {
  task: PropTypes.object.isRequired,
  handleSetStatusToComplete: PropTypes.func.isRequired,
  onClickEditTask: PropTypes.func,
  removeTask: PropTypes.func,
  playbooks: PropTypes.array.isRequired,
  requester: PropTypes.object.isRequired,
  skills: PropTypes.array.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  playbooks: state.playbooks.allPlaybooks,
  requester: state.user,
  skills: state.skills.allSkills,
});

export default connect(mapStateToProps, dispatch)(React.memo(TaskItem));
